import { toNumber, toNumberTimestamp, tickKey } from '../../../util.js';
export const getSummaryData = (
  data?: Record<string, unknown>[],
  props?: Record<string, unknown> & { colors?: Record<string, string> },
): Record<string, unknown> | Record<string, unknown>[] | undefined => {
  if (!data) return undefined;

  const {
    totals_key = props?.value_key,
    count_key,
    color_key,
    colors,
    include_keys = ['color'],
    include_items = false,
  } = props || {};

  const summary_data: Record<string, unknown> & {
    total: number;
    average: number | undefined;
    count: number;
    high: number | undefined;
    low: number | undefined;
    items: Record<string, unknown>[];
    color: string | undefined;
    indexes: number[];
  } = {
    total: 0,
    average: undefined,
    count: 0,
    high: undefined,
    low: undefined,
    items: [],
    color: undefined,
    indexes: [],
  };

  const carried = Array.isArray(include_keys) ? (include_keys as string[]) : [];

  const rows = Array.isArray(data) ? data : [];

  for (let i = 0; i < rows.length; i++) {
    const d = rows[i];

    if (d === null || typeof d !== 'object') continue;

    const count = d?.[count_key as string] === undefined ? 1 : toNumber(d[count_key as string]) ?? 0;

    summary_data.count += count;

    if (include_items) {
      summary_data.items.push({ ...d, index: i });
      summary_data.indexes.push(i);
    }

    for (let k = 0; k < carried.length; k++) {
      const key = carried[k];
      if (d?.[key] !== undefined) summary_data[key] = d?.[key];
    }

    if (totals_key) {
      const val = toNumber(d?.[totals_key as string]);
      if (val !== undefined) {
        summary_data.total += val;
        if (summary_data.high === undefined || val > summary_data.high) summary_data.high = val;
        if (summary_data.low === undefined || val < summary_data.low) summary_data.low = val;
      }
    }

    if (color_key && colors) {
      const color_val = d?.[color_key as string] as string;
      if (Object.hasOwn(colors, color_val) && colors[color_val] !== undefined) summary_data.color = colors[color_val];
    }
  }

  summary_data.average =
    summary_data.count > 0 ? summary_data.total / summary_data.count : undefined;

  return summary_data;
};

export const getWideSummaryData = (
  data?: Record<string, unknown>[],
  props?: Record<string, unknown> & { colors?: Record<string, string> },
): undefined | Record<string, unknown> | Record<string, unknown>[] => {
  let value_keys = props?.value_keys as string[] | undefined;

  if (!data || !value_keys) return undefined;

  const summary_data: Record<string, Record<string, unknown>> = Object.create(null);

  if (!Array.isArray(value_keys)) value_keys = [value_keys];

  const {
    flatten = true,
    colors,
    count_key,
    include_items = false,
  } = props || {};

  const rows = Array.isArray(data) ? data : [];

  for (let i = 0; i < rows.length; i++) {
    const item = rows[i];

    if (item === null || typeof item !== 'object') continue;

    for (let k = 0; k < value_keys.length; k++) {
      const key = value_keys[k];
      const color = colors && Object.hasOwn(colors, key) ? colors[key] : undefined;

      if (!summary_data?.[key])
        summary_data[key] = {
          total: 0,
          color,
          count: 0,
          average: undefined,
          high: undefined,
          low: undefined,
          items: [] as Record<string, unknown>[],
          indexes: [] as number[],
          group: key,
          group_key: 'group',
          totals_key: key,
        };

      const summary = summary_data[key];
      const val = toNumber(item?.[key]);
      const count = item?.[count_key as string] === undefined ? 1 : toNumber(item[count_key as string]) ?? 0;

      summary.count = (summary.count as number) + count;

      if (val !== undefined) {
        summary.total = (summary.total as number) + val;
        if (summary.high === undefined || val > (summary.high as number)) summary.high = val;
        if (summary.low === undefined || val < (summary.low as number)) summary.low = val;
      }

      if (include_items) {
        (summary.items as Record<string, unknown>[]).push({
          ...item,
          index: i,
        });
        (summary.indexes as number[]).push(i);
      }
    }
  }

  Object.values(summary_data).forEach((summary) => {
    summary.average =
      (summary.count as number) > 0
        ? (summary.total as number) / (summary.count as number)
        : undefined;
  });

  return flatten ? Object.values(summary_data || {}) : summary_data;
};

export const getLongSummaryData = (
  data?: Record<string, unknown>[],
  props?: Record<string, unknown>,
): Record<string, unknown>[] | Record<string, unknown> => {
  const summary_totals: Record<string, unknown> = Object.create(null);
  const flat_data = [] as Record<string, unknown>[];

  const {
    grouping_key,
    grouping_keys,
    flatten = true,
    totals_key,
    include_keys = ['color'],
  } = props || {};

  props = { ...props, include_keys, totals_key };

  const keys = Array.isArray(grouping_keys)
    ? (grouping_keys as string[])
    : Array.isArray(grouping_key)
      ? (grouping_key as unknown as string[])
      : undefined;

  const grouped_data: Record<string, Record<string, unknown>[]> = Object.create(null);
  const group_values = new Map<string, string[]>();

  const rows = Array.isArray(data) ? data : [];

  for (let i = 0; i < rows.length; i++) {
    const item = rows[i];

    if (item === null || typeof item !== 'object') continue;

    const values = keys ? keys.map((key) => String(item?.[key])) : [String(item?.[grouping_key as string])];
    const group_id = keys ? JSON.stringify(values) : values[0];
    group_values.set(group_id, values);

    if (!grouped_data?.[group_id]) grouped_data[group_id] = [];

    grouped_data[group_id].push({ ...item, group: group_id, index: i });
  }

  Object.entries(grouped_data).forEach(([group_id, items]) => {
    const group_summary = getSummaryData(items as Record<string, unknown>[], {
      ...props,
      totals_key,
      grouping_key: 'group',
    });

    let new_item = {
      grouping_key,
      group: group_id,
      ...group_summary,
    } as Record<string, unknown>;

    if (keys) {
      const groups = group_values.get(group_id)!;
      keys.forEach((k, index) => {
        new_item = { ...new_item, [k]: groups?.[index] };
      });
    } else if (grouping_key) {
      new_item = { ...new_item, [grouping_key as string]: group_id };
    }

    summary_totals[group_id] = new_item;

    if (flatten) flat_data.push(new_item);
  });

  return flatten ? flat_data : summary_totals;
};

export const getGroupSummaryData = (
  data?: Record<string, unknown>[],
  props?: Record<string, unknown>,
): Record<string, unknown>[] | Record<string, unknown> | undefined =>
  props?.value_keys
    ? getWideSummaryData(data, props)
    : getLongSummaryData(data, props);

export const getMinMax = ({
  data,
  timestamp_key,
  totals_key,
  value_keys,
  ...props
}: Record<string, unknown>): number[] | undefined => {
  const rows = Array.isArray(data) ? data : [];

  const wide = Array.isArray(value_keys)
    ? (value_keys as string[]).filter((key) => typeof key === 'string' && key)
    : typeof value_keys === 'string' && value_keys
      ? [value_keys as string]
      : [];

  const use_time = !totals_key && !wide.length && timestamp_key;

  const keys = use_time
    ? [timestamp_key as string]
    : wide.length
      ? wide
      : typeof totals_key === 'string' && totals_key
        ? [totals_key]
        : [];

  if (!keys.length) return undefined;

  let min: number | undefined = undefined;
  let max: number | undefined = undefined;

  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;

    for (const key of keys) {
      const parsed = use_time
        ? toNumberTimestamp(row[key])
        : toNumber(row[key]);

      if (parsed === undefined) continue;
      if (min === undefined || parsed < min) min = parsed;
      if (max === undefined || parsed > max) max = parsed;
    }
  }

  if (min === undefined || max === undefined) return undefined;

  if (!use_time) {
    const floor = toNumber(props?.min);
    const ceiling = toNumber(props?.max);

    if (floor !== undefined) min = floor;
    if (ceiling !== undefined) max = ceiling;
  }

  return [min, max];
};

export const getMinMaxTime = ({
  data,
  timestamp_key,
  start_key,
  end_key,
  ...props
}: Record<string, unknown>): number[] | undefined => {
  const rows = Array.isArray(data) ? data : [];

  const keys = [timestamp_key, start_key, end_key].filter(
    (key) => typeof key === 'string' && key !== '',
  ) as string[];

  if (!keys.length) return undefined;


  let min: number | undefined = undefined;
  let max: number | undefined = undefined;

  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;

    for (const key of keys) {
      const time = toNumberTimestamp(row[key]);

      if (time === undefined) continue;
      if (min === undefined || time < min) min = time;
      if (max === undefined || time > max) max = time;
    }
  }

  const floor = toNumberTimestamp(props?.min);
  const ceiling = toNumberTimestamp(props?.max);

  if (floor !== undefined) min = floor;
  if (ceiling !== undefined) max = ceiling;

  if (min === undefined || max === undefined) return undefined;

  return [min, max];
};

export const getStackedMinMax = ({
  data,
  value_keys,
  totals_key,
  stack_key,
  ...props
}: Record<string, unknown>): number[] | undefined => {
  const rows = Array.isArray(data) ? data : [];
  const keys = Array.isArray(value_keys) ? value_keys : [];

  if (!keys.length && !totals_key) return undefined;

  const totals: Record<string, { up: number; down: number }> = Object.create(null);

  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;

    const at = stack_key === undefined ? '' : tickKey(row[stack_key as string]);

    const values = keys.length
      ? keys.map((key) => toNumber(row[key]))
      : [toNumber(row[totals_key as string])];

    for (const parsed of values) {
      if (parsed === undefined) continue;
      if (!totals[at]) totals[at] = { up: 0, down: 0 };
      if (parsed < 0) totals[at].down = totals[at].down + parsed;
      else totals[at].up = totals[at].up + parsed;
    }
  }

  const stacks = Object.values(totals);

  if (!stacks.length) return undefined;

  let min = 0;
  let max = 0;

  for (const stack of stacks) {
    if (stack.down < min) min = stack.down;
    if (stack.up > max) max = stack.up;
  }

  const floor = toNumber(props?.min);
  const ceiling = toNumber(props?.max);

  if (floor !== undefined) min = floor;
  if (ceiling !== undefined) max = ceiling;

  return [min, max];
};

export const add = (
  total: number | undefined,
  value: number | undefined,
): number | undefined => {
  if (value === undefined) return total;
  return total === undefined ? value : total + value;
};
